import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

const MAX_NO_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 1);

const EMPTY_FORM = {
  optitem_id:  '',
  opt_type:    '',   // 1=Free, 2=Paid
  rate_type:   '/day',
  opt_dayrate: '',
  opt_max:     1,
  min_bkngdys: '',
};

export default function OptionalExtraDetail() {
  const navigate = useNavigate();

  // ── Meta ──
  const [optItems,  setOptItems]  = useState([]);  // tbl_optextraitems
  const [allGroups, setAllGroups] = useState([]);  // tbl_group

  // ── Table ──
  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);

  // ── Form ──
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [selGroups, setSelGroups] = useState([]);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);

  // ── View modal ──
  const [viewItem, setViewItem] = useState(null);

  // ── Auth ──
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/login');
  }, []);

  // ── Load dropdowns ──
  useEffect(() => {
    API.get('/optional-extras/items')
      .then(r => setOptItems(r.data))
      .catch(() => toast.error('Failed to load optional extra items'));

    API.get('/optional-extras/groups')
      .then(r => setAllGroups(r.data))
      .catch(() => toast.error('Failed to load groups'));
  }, []);

  // ── Fetch table ──
  const fetchData = useCallback((pg = page, q = search) => {
    setLoading(true);
    API.get('/optional-extras', { params: { page: pg, limit: 10, search: q } })
      .then(r => {
        setData(r.data.data);
        setTotalPages(r.data.totalPages);
        setTotal(r.data.total);
      })
      .catch(() => toast.error('Failed to load records'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Form change ──
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      // Reset rate fields when switching to Free
      if (name === 'opt_type' && value === '1') {
        updated.opt_dayrate = '';
        updated.rate_type   = '/day';
        updated.min_bkngdys = '';
      }
      return updated;
    });
  };

  const toggleGroup = gid => {
    setSelGroups(prev =>
      prev.includes(gid) ? prev.filter(x => x !== gid) : [...prev, gid]
    );
  };

  // ── Reset form ──
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSelGroups([]);
    setEditId(null);
  };

  // ── Edit ──
  const handleEdit = (row) => {
    setEditId(row.optdet_id);
    setForm({
      optitem_id:  row.optitem_id,
      opt_type:    String(row.opt_type),
      rate_type:   row.rate_type   || '/day',
      opt_dayrate: row.opt_dayrate || '',
      opt_max:     row.opt_max     || 1,
      min_bkngdys: row.min_bkngdys || '',
    });
    setSelGroups((row.groups || []).map(g => g.group_id));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!form.optitem_id)    { toast.warning('Select an optional extra item');  return; }
    if (!form.opt_type)      { toast.warning('Select a payment option');        return; }
    if (form.opt_type === '2' && !form.opt_dayrate) {
      toast.warning('Enter rate for paid option'); return;
    }
    if (!selGroups.length)   { toast.warning('Select at least one car group'); return; }

    setSaving(true);
    try {
      const payload = {
        optitem_id:  parseInt(form.optitem_id),
        opt_type:    parseInt(form.opt_type),
        rate_type:   form.rate_type,
        opt_dayrate: form.opt_type === '2' ? parseFloat(form.opt_dayrate) : 0,
        opt_max:     parseInt(form.opt_max),
        min_bkngdys: form.min_bkngdys ? parseInt(form.min_bkngdys) : 0,
        group_ids:   selGroups,
      };

      if (editId) {
        await API.put(`/optional-extras/${editId}`, payload);
        toast.success('Optional extra updated successfully');
      } else {
        await API.post('/optional-extras', payload);
        toast.success('Optional extra added successfully');
      }
      resetForm();
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
    setSaving(false);
  };

  // ── Delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this optional extra record?')) return;
    try {
      await API.delete(`/optional-extras/${id}`);
      toast.success('Deleted successfully');
      fetchData(page, search);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const isPaid = form.opt_type === '2';

  const paymentLabel = (type) => {
    if (type == 1) return { label: 'Free',  bg: '#f0fff6', color: '#1a9e5c', border: '#b8f0d0' };
    if (type == 2) return { label: 'Paid',  bg: '#f3f2ff', color: '#6C63FF', border: '#d4d0ff' };
    return                 { label: '—',    bg: '#f8f8f8', color: '#aaa',    border: '#e0e0e0' };
  };

  return (
    <Layout activePage="Optional Extra Details">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Add / Edit Form Card ── */}
      <div style={S.formCard}>
        <div style={S.formCardHead}>
          <div>
            <h2 style={S.formTitle}>
              {editId ? '✏️ Edit Optional Extra Details' : '➕ Add Optional Extra Details'}
            </h2>
            {editId && <span style={S.editBadge}>Editing #{editId}</span>}
          </div>
          {editId && (
            <button style={S.cancelBtn} onClick={resetForm}>✕ Cancel Edit</button>
          )}
        </div>

        {/* Row 1: Optional Extra + Payment Option */}
        <div style={S.grid2}>
          <div style={S.fg}>
            <label style={S.label}>Optional Extra Item <span style={S.req}>*</span></label>
            <select name="optitem_id" value={form.optitem_id} onChange={handleChange} style={S.select}>
              <option value="">— Select Optional Extra —</option>
              {optItems.map(o => (
                <option key={o.optitem_id} value={o.optitem_id}>{o.optitem_name}</option>
              ))}
            </select>
          </div>
          <div style={S.fg}>
            <label style={S.label}>Payment Option <span style={S.req}>*</span></label>
            <select name="opt_type" value={form.opt_type} onChange={handleChange} style={S.select}>
              <option value="">— Select —</option>
              <option value="1">Free</option>
              <option value="2">Paid</option>
            </select>
          </div>
        </div>

        {/* Row 2: Rate fields — shown only for Paid */}
        {isPaid && (
          <div style={{ ...S.grid3, marginTop: 14 }}>
            <div style={S.fg}>
              <label style={S.label}>Rate Type <span style={S.req}>*</span></label>
              <select name="rate_type" value={form.rate_type} onChange={handleChange} style={S.select}>
                <option value="/day">Per Day</option>
                <option value="/Once">One Time</option>
              </select>
            </div>
            <div style={S.fg}>
              <label style={S.label}>Rate <span style={S.req}>*</span></label>
              <input
                type="number" name="opt_dayrate" min="0" step="0.01"
                value={form.opt_dayrate} onChange={handleChange}
                placeholder="e.g. 5.00" style={S.input}
              />
            </div>
            <div style={S.fg}>
              <label style={S.label}>Min. Booking Days</label>
              <input
                type="number" name="min_bkngdys" min="0"
                value={form.min_bkngdys} onChange={handleChange}
                placeholder="e.g. 1" style={S.input}
              />
            </div>
          </div>
        )}

        {/* Row 3: Max No */}
        <div style={{ ...S.grid2, marginTop: 14 }}>
          <div style={S.fg}>
            <label style={S.label}>Maximum Quantity</label>
            <select name="opt_max" value={form.opt_max} onChange={handleChange} style={S.select}>
              {MAX_NO_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 4: Car Groups */}
        <div style={{ marginTop: 18 }}>
          <div style={S.sectionLabel}>
            Car Groups <span style={S.req}>*</span>
          </div>
          <div style={S.groupGrid}>
            {allGroups.map(g => {
              const checked = selGroups.includes(g.group_id);
              return (
                <label key={g.group_id} style={{
                  ...S.checkLabel,
                  background: checked ? '#f3f2ff' : '#fafafa',
                  border:     `1.5px solid ${checked ? '#6C63FF' : '#e0e0e0'}`,
                  color:      checked ? '#6C63FF' : '#555',
                }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleGroup(g.group_id)}
                    style={{ accentColor: '#6C63FF' }}
                  />
                  {g.group_name}
                </label>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          {editId && <button style={S.cancelBtn} onClick={resetForm}>Clear</button>}
          <button style={S.submitBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : editId ? 'Update' : 'Add Optional Extra'}
          </button>
        </div>
      </div>

      {/* ── Manage Table Card ── */}
      <div style={{ ...S.card, marginTop: 24 }}>

        {/* Table header + search */}
        <div style={S.tableHead}>
          <span style={S.tableTitle}>Manage Optional Extra Details</span>
          <div style={S.searchWrap}>
            <span style={{ fontSize: 13, color: '#aaa' }}>🔍</span>
            <input type="text" placeholder="Search item name..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={S.searchInput} />
            {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Optional Extra</th>
                <th style={S.th}>Payment</th>
                <th style={S.th}>Rate</th>
                <th style={S.th}>Rate Type</th>
                <th style={S.th}>Max Qty</th>
                <th style={S.th}>Min Days</th>
                <th style={S.th}>Car Groups</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={9} style={S.emptyCell}>No records available</td></tr>
              ) : data.map((row, i) => {
                const pay = paymentLabel(row.opt_type);
                return (
                  <tr key={row.optdet_id} style={{
                    ...S.tr,
                    background: editId === row.optdet_id ? '#f3f2ff' : 'transparent'
                  }}>
                    <td style={S.td}>
                      <span style={S.serial}>{(page - 1) * 10 + i + 1}</span>
                    </td>
                    <td style={S.td}>
                      <span style={S.nameChip}>{row.optitem_name}</span>
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, background: pay.bg, color: pay.color, border: `1px solid ${pay.border}` }}>
                        {pay.label}
                      </span>
                    </td>
                    <td style={S.td}>
                      {row.opt_type == 2
                        ? <span style={S.priceChip}>{row.opt_dayrate}</span>
                        : <span style={{ color: '#aaa', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: '#777' }}>
                      {row.opt_type == 2 ? (row.rate_type || '—') : '—'}
                    </td>
                    <td style={S.td}>
                      <span style={S.numChip}>{row.opt_max}</span>
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: '#777' }}>
                      {row.min_bkngdys > 0 ? `${row.min_bkngdys} days` : '—'}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(row.groups || []).map(g => (
                          <span key={g.group_id} style={S.groupTag}>{g.group_name}</span>
                        ))}
                        {!row.groups?.length && <span style={{ color: '#ccc', fontSize: 11 }}>—</span>}
                      </div>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.viewBtn} onClick={() => setViewItem(row)}>View</button>
                      <button style={S.editBtn} onClick={() => handleEdit(row)}>Edit</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(row.optdet_id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={S.pagination}>
          <span style={S.paginInfo}>
            {total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10, total)} of ${total}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0}
              onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── View Modal ── */}
      {viewItem && (
        <div style={S.overlay} onClick={() => setViewItem(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                🔧 {viewItem.optitem_name}
              </h3>
              <button style={S.closeBtn} onClick={() => setViewItem(null)}>✕</button>
            </div>
            <div style={S.modalBody}>
              {[
                ['Payment Option',    viewItem.opt_type == 1 ? 'Free' : 'Paid'],
                ['Rate',              viewItem.opt_type == 2 ? viewItem.opt_dayrate : '—'],
                ['Rate Type',         viewItem.opt_type == 2 ? viewItem.rate_type : '—'],
                ['Max Quantity',      viewItem.opt_max],
                ['Min Booking Days',  viewItem.min_bkngdys > 0 ? `${viewItem.min_bkngdys} days` : '—'],
              ].map(([k, v]) => (
                <div key={k} style={S.viewRow}>
                  <span style={S.viewKey}>{k}</span>
                  <span style={{ fontSize: 13, color: '#333', fontWeight: 600 }}>{v}</span>
                </div>
              ))}

              <div style={{ ...S.sectionLabel, marginTop: 16 }}>Assigned Car Groups</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {(viewItem.groups || []).map(g => (
                  <span key={g.group_id} style={{ ...S.groupTag, fontSize: 13, padding: '5px 14px' }}>
                    {g.group_name}
                  </span>
                ))}
                {!viewItem.groups?.length && (
                  <span style={{ color: '#bbb', fontSize: 13 }}>No groups assigned</span>
                )}
              </div>
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ─── Styles ─── */
const S = {
  formCard:     { background: '#fff', borderRadius: 16, padding: '24px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)' },
  formCardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  formTitle:    { margin: 0, fontSize: 20, fontWeight: 800, color: '#1a1d2e' },
  editBadge:    { fontSize: 11, fontWeight: 700, background: '#f3f2ff', color: '#6C63FF',
    borderRadius: 20, padding: '3px 10px', display: 'inline-block', marginTop: 4 },
  sectionLabel: { fontSize: 11, fontWeight: 800, color: '#6C63FF',
    textTransform: 'uppercase', letterSpacing: '0.7px',
    borderBottom: '1.5px solid #f3f2ff', paddingBottom: 6, marginBottom: 12 },
  groupGrid:    { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  checkLabel:   { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
    borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    transition: 'all 0.15s', userSelect: 'none' },

  card:       { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 12 },
  tableTitle: { fontSize: 14, fontWeight: 700, color: '#1a1d2e' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: 8, background: '#f8f8f8',
    border: '1px solid #e8e8e8', borderRadius: 10, padding: '7px 14px', minWidth: 220 },
  searchInput:{ border: 'none', outline: 'none', fontSize: 13, background: 'transparent',
    fontFamily: 'inherit', color: '#333', flex: 1 },
  clearBtn:   { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },

  table:      { width: '100%', borderCollapse: 'collapse' },
  theadRow:   { background: '#fafafa' },
  th:         { padding: '11px 14px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'left', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr:         { transition: 'background 0.12s' },
  td:         { padding: '12px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:  { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:     { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  nameChip:   { background: '#f0fff6', color: '#1a9e5c', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  priceChip:  { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  numChip:    { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  badge:      { borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },
  groupTag:   { background: '#f0f8ff', color: '#1a6eb5', border: '1px solid #cce5ff',
    borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 },

  viewBtn:    { marginRight: 5, background: '#f0fff6', color: '#1a9e5c',
    border: '1px solid #b8f0d0', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  editBtn:    { marginRight: 5, background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', padding: '4px 12px',
    borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:     { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },

  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:  { fontSize: 12, color: '#999' },
  pgBtn:      { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF',
    border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  overlay:    { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.55)', zIndex: 999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:   { background: '#fff', borderRadius: 20, width: 520, maxWidth: '96vw', maxHeight: '88vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' },
  modalHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:  { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:  { display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:   { background: '#ff6b6b22', border: 'none', color: '#ff6b6b',
    borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },

  viewRow:    { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, paddingBottom: 10,
    borderBottom: '1px solid #f8f8f8' },
  viewKey:    { fontSize: 13, fontWeight: 600, color: '#aaa', minWidth: 150 },

  cancelBtn:  { padding: '9px 18px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:  { padding: '10px 24px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
  fg:      { display: 'flex', flexDirection: 'column', gap: 5 },
  label:   { fontSize: 12, fontWeight: 600, color: '#555' },
  req:     { color: '#ff6b6b' },
  select:  { padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    background: '#fff', width: '100%', boxSizing: 'border-box', cursor: 'pointer' },
  input:   { padding: '9px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box' },
};
